/**
 * Custom element for collapsing richtext content with a "Read more" button.
 * 
 * @extends {HTMLElement}
 */

class TabRichtextReadmore extends HTMLElement {
  /**
   * Initializes DOM references, observers, and event listeners whenever
   * the custom element is attached to the document.
   */
  connectedCallback() {
    this.teardown();

    this.readmoreContent = this.querySelector('[data-readmore-target]');
    this.toggleButton = this.querySelector('[data-readmore-toggle]');
    this.moreLabel = this.querySelector('.tab-richtext-readmore__label--more');
    this.lessLabel = this.querySelector('.tab-richtext-readmore__label--less');

    if (!this.readmoreContent || !this.toggleButton) return;

    const collapsedMaxValue = this.dataset.collapsedMax || '125';
    const parsedCollapsedMax = parseInt(collapsedMaxValue, 10);
    const normalizedCollapsedMax = Number.isFinite(parsedCollapsedMax) ? parsedCollapsedMax : 125;
    // Theme editor typically sends 80–500px; clamp for safety if data attribute is tampered with.
    this.collapsedMaxHeight = Math.max(60, Math.min(800, normalizedCollapsedMax));
    this.style.setProperty('--tab-readmore-collapsed', `${this.collapsedMaxHeight}px`);

    this.parentTabPanel = this.closest('[role="tabpanel"]');
    this.parentDetails = this.closest('details');

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleWindowResize = () => this.scheduleClampMeasurement();
    this.toggleButton.addEventListener('click', this.handleToggleClick);
    window.addEventListener('resize', this.handleWindowResize);

    this.resizeObserver = new ResizeObserver(() => this.scheduleClampMeasurement());
    this.resizeObserver.observe(this.readmoreContent);

    const observerOptions = { attributes: true, attributeFilter: ['hidden', 'open'] };
    this.tabPanelObserver = null;
    if (this.parentTabPanel) {
      this.tabPanelObserver = new MutationObserver(() => this.scheduleClampMeasurement());
      this.tabPanelObserver.observe(this.parentTabPanel, observerOptions);
    }
    this.detailsObserver = null;
    if (this.parentDetails) {
      this.detailsObserver = new MutationObserver(() => this.scheduleClampMeasurement());
      this.detailsObserver.observe(this.parentDetails, observerOptions);
    }

    this.isMeasurementScheduled = false;
    this.scheduleClampMeasurement();
  }

  /**
   * Cleans up listeners/observers when the element is detached.
   */
  disconnectedCallback() {
    this.teardown();
  }

  /**
   * Removes event listeners and disconnects observers to prevent leaks and
   * duplicate subscriptions during reconnects.
   */
  teardown() {
    this.toggleButton?.removeEventListener('click', this.handleToggleClick);
    window.removeEventListener('resize', this.handleWindowResize);
    this.resizeObserver?.disconnect();
    this.tabPanelObserver?.disconnect();
    this.detailsObserver?.disconnect();
    this.resizeObserver = this.tabPanelObserver = this.detailsObserver = null;
  }

  /**
   * Schedules a single clamp-state calculation on the next animation frame.
   * Multiple rapid triggers are coalesced into one measurement pass.
   */
  scheduleClampMeasurement() {
    if (this.isMeasurementScheduled) return;
    this.isMeasurementScheduled = true;
    requestAnimationFrame(() => {
      this.isMeasurementScheduled = false;
      this.updateClampState();
    });
  }

  /**
   * Desktop/tablet uses the theme `md` breakpoint (768px); mobile is below that.
   */
  isReadmoreEnabledForViewport() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop) {
      return this.dataset.showOnDesktop !== 'false';
    }
    return this.dataset.showOnMobile !== 'false';
  }

  /**
   * Computes whether content exceeds the collapsed threshold and updates
   * visual/ARIA state for clamp and toggle labels.
   */
  updateClampState() {
    if (!this.readmoreContent || !this.toggleButton) return;

    if (this.parentTabPanel?.hidden || (this.parentDetails && !this.parentDetails.open)) {
      return;
    }

    if (!this.isReadmoreEnabledForViewport()) {
      this.classList.remove('tab-richtext-readmore--clamp');
      this.toggleButton.hidden = true;
      return;
    }

    if (this.readmoreContent.classList.contains('is-expanded')) {
      this.classList.add('tab-richtext-readmore--expanded');
      this.classList.remove('tab-richtext-readmore--clamp');
      this.toggleButton.hidden = false;
      return;
    }

    this.classList.remove('tab-richtext-readmore--expanded');

    this.classList.remove('tab-richtext-readmore--clamp');

    // Temporarily remove max-height so scrollHeight reflects full content.
    const previousMaxHeight = this.readmoreContent.style.maxHeight;
    this.readmoreContent.style.maxHeight = 'none';
    const contentScrollHeight = this.readmoreContent.scrollHeight;
    this.readmoreContent.style.maxHeight = previousMaxHeight;

    const shouldClampContent = contentScrollHeight > this.collapsedMaxHeight;
    this.classList.toggle('tab-richtext-readmore--clamp', shouldClampContent);
    this.toggleButton.hidden = !shouldClampContent;

    if (!shouldClampContent) {
      this.toggleButton.setAttribute('aria-expanded', 'false');
      if (this.moreLabel) this.moreLabel.hidden = false;
      if (this.lessLabel) this.lessLabel.hidden = true;
    }
  }

  /**
   * Toggles expanded state from user interaction and syncs labels/ARIA.
   *
   * @param {MouseEvent} clickEvent
   */
  handleToggleClick(clickEvent) {
    clickEvent.preventDefault();
    const isExpanded = this.readmoreContent.classList.toggle('is-expanded');
    this.toggleButton.setAttribute('aria-expanded', isExpanded);

    if (this.moreLabel) this.moreLabel.hidden = isExpanded;
    if (this.lessLabel) this.lessLabel.hidden = !isExpanded;

    this.updateClampState();
  }
}

if (!customElements.get('tab-richtext-readmore')) {
  customElements.define('tab-richtext-readmore', TabRichtextReadmore);
}
